import React, { ReactNode, useEffect } from "react";
import classNames from "classnames";
import ReactDOM from "react-dom";

import { overlaysRef } from "src/App";
import useLayerEscapeClose from "src/hooks/useLayerEscapeClose";
import { Button, ButtonVariantDefault, ButtonVariants } from "../Button/Button";
import Loader from "../Loader/Loader";
import { ReactComponent as ArrowLeftIcon } from "../../../images/arrow-left.svg";
import IDHFormattedMessage from "../IDHFormattedMessage/IDHFormattedMessage";
import layerManager from "src/utils/layerManager";
import { closeIndependentDropdowns } from "src/utils/dropdownEvents";

import { Dispatch } from "redux";
import { setOutsideClickWarningModalVisibility } from "src/redux";
import { connect } from "react-redux";

import "./Modal.scss";

type ModalVariants = "small" | "medium";
type PositionActionButtons = "left" | "right" | "space-between";
export enum OutsideClickModes {
  Allow = "allow",
  Warn = "warn",
}

export const INPUT_TYPES_TO_FOCUS = [
  "text",
  "number",
  "email",
  "search",
  "password",
];

interface ModalContentProps {
  onClose?: () => void;
  onClick?: any;
  disableTransition?: boolean;
  children?: React.ReactNode;
  variant?: ModalVariants;
  className: string;
  disableClickOutside?: boolean;
  props?: any;
  displayCancelButton?: boolean;
  onCancelClick?: any;
  cancelButtonIcon?: React.SVGProps<SVGSVGElement>;
  closeButtonText?: React.ReactNode | string;
  closeButtonVariant?: ButtonVariants;
  onConfirmClick?: any;
  confirmButtonLoading?: boolean;
  confirmButtonDisabled?: boolean;
  confirmButtonText?: React.ReactNode | string;
  confirmButtonVariant?: ButtonVariants;
  buttonCentered?: boolean;
  buttonsHidden?: boolean;
  customButton?: React.ReactNode;
  imgSrc?: string;
  title?: any;
  description?: ReactNode;
  isLoading?: boolean;
  positionActionButtons?: PositionActionButtons;
  disableAutoFocusForSelectors?: boolean;
}

interface ModalProps extends ModalContentProps {
  overlayClassName?: string;
  outsideClick?: OutsideClickModes;

  //redux
  setOutsideClickWarningModalVisibility: (value: boolean) => void;
}

class Modal extends React.Component<ModalProps> {
  el = document.createElement("div");

  componentDidMount() {
    closeIndependentDropdowns();

    const overlayClassNames = [
      "modal-overlay",
      ...(!this.props.disableTransition ? ["modal-overlay--hidden"] : []),
    ];

    if (this.props.outsideClick) {
      this.el.addEventListener("click", this.closeOnOutsideClick);
    }

    this.el.classList.add(...overlayClassNames);
    if (this.props.overlayClassName) {
      this.el.classList.add(this.props.overlayClassName);
    }
    overlaysRef.current.appendChild(this.el);
    document.body.classList.add("block-ui");
    setTimeout(() => {
      this.el.classList.remove("modal-overlay--hidden");
    }, 10);
  }

  componentWillUnmount() {
    overlaysRef.current.removeChild(this.el);
    document.body.classList.remove("block-ui");
    this.el.removeEventListener("click", this.closeOnOutsideClick);
  }

  closeOnOutsideClick = (e: MouseEvent) => {
    if (e.target !== this.el) return;

    if (this.props.outsideClick === OutsideClickModes.Allow) {
      layerManager.handleEscape();
    } else if (this.props.outsideClick === OutsideClickModes.Warn) {
      this.props.setOutsideClickWarningModalVisibility(true);
    }
  };

  render() {
    const { overlayClassName, ...otherProps } = this.props;
    return ReactDOM.createPortal(<ModalContent {...otherProps} />, this.el);
  }
}

const ModalContent: React.FC<ModalContentProps> = ({
  onClose,
  onClick,
  disableTransition,
  children,
  variant,
  className,
  disableClickOutside,
  displayCancelButton,
  closeButtonText,
  closeButtonVariant,
  onCancelClick,
  cancelButtonIcon,
  onConfirmClick,
  confirmButtonLoading,
  confirmButtonDisabled,
  confirmButtonText,
  confirmButtonVariant,
  buttonCentered,
  buttonsHidden,
  customButton,
  imgSrc,
  title,
  isLoading,
  description,
  positionActionButtons = "space-between",
  disableAutoFocusForSelectors,
  ...props
}) => {
  useLayerEscapeClose(onClose ?? (() => {}));

  useEffect(() => {
    const focusOnFirstInput = () => {
      const inputs: NodeListOf<HTMLInputElement> = document.querySelectorAll(
        `.${className} .modal__body input`,
      );

      for (const input of Array.from(inputs)) {
        if (
          INPUT_TYPES_TO_FOCUS.includes(input.type) &&
          (!disableAutoFocusForSelectors || !input.id.includes("react-select"))
        ) {
          return setTimeout(() => {
            input.focus();
          }, 300);
        }
      }
    };

    const timerId = focusOnFirstInput();

    return () => {
      if (timerId !== undefined) {
        clearTimeout(timerId);
      }
    };
  }, [className, disableAutoFocusForSelectors]);

  return (
    <div
      onClick={onClick}
      className={classNames(
        "modal",
        {
          "modal--small": variant === "small",
          "modal--medium": variant === "medium",
          "modal--with-img": imgSrc,
          "modal--buttons-left": positionActionButtons === "left",
          "modal--buttons-right": positionActionButtons === "right",
          "modal--buttons-space-between":
            positionActionButtons === "space-between",
        },
        className,
      )}
      {...props}
    >
      {isLoading ? (
        <Loader />
      ) : (
        <>
          {title && <ModalTitle>{title}</ModalTitle>}
          {description && <ModalText>{description}</ModalText>}
          {imgSrc && <img src={imgSrc} className="modal__img" />}
          <div
            className={classNames("modal__body-wrapper", {
              "modal__content-with-img": imgSrc,
            })}
          >
            <div className="modal__body">{children}</div>
            {!buttonsHidden && (
              <div
                className={classNames("modal__buttons", {
                  "modal__buttons--centered": buttonCentered,
                })}
              >
                {displayCancelButton && (
                  <Button
                    type="button"
                    variant={closeButtonVariant || "white-with-border"}
                    size="large"
                    onClick={onCancelClick || onClose}
                    data-qa="cancel-button"
                  >
                    {cancelButtonIcon || null}
                    {typeof closeButtonText !== "undefined" ? (
                      closeButtonText
                    ) : (
                      <IDHFormattedMessage
                        id="ws_close"
                        defaultMessage="Close"
                      />
                    )}
                  </Button>
                )}
                {onConfirmClick &&
                  (confirmButtonLoading ? (
                    <Loader />
                  ) : (
                    <Button
                      variant={
                        typeof confirmButtonVariant === "undefined"
                          ? ButtonVariantDefault
                          : confirmButtonVariant
                      }
                      size="large"
                      type="submit"
                      onClick={onConfirmClick}
                      disabled={confirmButtonDisabled}
                      data-qa="confirm-button"
                    >
                      {typeof confirmButtonText !== "undefined" ? (
                        confirmButtonText
                      ) : (
                        <IDHFormattedMessage
                          id="ws_confirm"
                          defaultMessage="Confirm"
                        />
                      )}
                    </Button>
                  ))}
                {customButton}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setOutsideClickWarningModalVisibility: (value: boolean) =>
    dispatch(setOutsideClickWarningModalVisibility(value)),
});

export default connect(null, mapDispatchToProps)(Modal);

interface ModalTitleProps {
  children: React.ReactNode;
  icon?: React.SVGProps<SVGSVGElement>;
  backBtnFunc?: () => void;
}

export const ModalTitle: React.FC<ModalTitleProps> = ({
  children,
  icon,
  backBtnFunc,
}) => {
  return (
    <h4
      className={classNames("modal__title", {
        "modal__title--with-back-btn": backBtnFunc,
      })}
    >
      {icon && icon}
      {backBtnFunc && (
        <div className="modal__title-back-btn" onClick={backBtnFunc}>
          <ArrowLeftIcon className="modal__title-back-btn-icon" />
        </div>
      )}
      {children}
    </h4>
  );
};

interface ModalScrollerProps {
  children: React.ReactNode;
  maxHeight?: number | string;
  className?: string;
}

export const ModalScroller: React.FC<ModalScrollerProps> = (props) => {
  const { children, maxHeight, className } = props;

  return (
    <div
      className={classNames("modal__scroller", className)}
      style={{ maxHeight }}
    >
      {children}
    </div>
  );
};

interface ModalTextProps {
  children: React.ReactNode;
}

export const ModalText: React.FC<ModalTextProps> = ({ children }) => {
  return <p className="modal__text">{children}</p>;
};
interface ModalButtonsProps {
  children: React.ReactNode;
}

export const ModalButtons: React.FC<ModalButtonsProps> = ({ children }) => {
  return <div className="modal__buttons">{children}</div>;
};

export const hideModal = (
  hidingFunction: () => void,
  overlayClassName?: string,
  disableTransition?: boolean,
) => {
  const className = overlayClassName || "modal-overlay";

  document
    .querySelector(`.${className}`)
    ?.classList.add("modal-overlay--hidden");

  if (disableTransition) {
    hidingFunction();
  } else {
    setTimeout(() => {
      hidingFunction();
    }, 200);
  }
};

interface ModalRowOrColumnProps {
  children: React.ReactNode;
  className?: string;
}

export const ModalRow: React.FC<ModalRowOrColumnProps> = ({
  children,
  className,
}) => {
  return <div className={classNames("modal__row", className)}>{children}</div>;
};

export const ModalColumn: React.FC<ModalRowOrColumnProps> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div className={classNames("modal__column", className)} {...props}>
      {children}
    </div>
  );
};

interface ModalHeadingProps {
  children: ReactNode;
  variant?: "medium" | "big";
}

export function ModalHeading(props: ModalHeadingProps) {
  const { children, variant = "medium" } = props;

  if (variant === "big") {
    return <h4 className="modal__heading modal__heading--big">{children}</h4>;
  }

  return <h6 className="modal__heading">{children}</h6>;
}
